import { useAtomValue } from 'jotai';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { CardHeader, Row, Col } from 'reactstrap';

import {
  closedVulnMetricsBySeverityCountsAtom,
  openVulnMetricsBySeverityCountsAtom,
  closedVulnMetricsByTimeCountsAtom,
  openVulnMetricsByTimeCountsAtom,
  metricsViewAtom,
  timeViewLabels,
  openVulnMetricsTooltipAtom,
  closedVulnMetricsTooltipAtom,
  openVulnMetricsBySeverityAverageDayTotalVulnAtom,
  closeVulnMetricsBySeverityAverageDayTotalVulnAtom,
} from './state';
import BarOrDoughnutChart from '../../components/BarOrDoughnutChart';
import { getSeverityValue, getTimeLabelValue, mergeArrays } from '../../helper';
import { ChartType, Severity, MetricsView } from '../../types';
import { getDrillDownUrl } from '../helper';
import {
  appliedHostIdsAtom,
  criticalityFiltersAtom,
  policyIdFilterAtom,
} from '../state';

const Charts = () => {
  const openVulnMetricsBySeverityAverageDayTotalVulnData = useAtomValue(
    openVulnMetricsBySeverityAverageDayTotalVulnAtom,
  );
  const closeVulnMetricsBySeverityAverageDayTotalVulnData = useAtomValue(
    closeVulnMetricsBySeverityAverageDayTotalVulnAtom,
  );
  const openVulnMetricsBySeverityCountsData = useAtomValue(
    openVulnMetricsBySeverityCountsAtom,
  );
  const closedVulnMetricsBySeverityCountsData = useAtomValue(
    closedVulnMetricsBySeverityCountsAtom,
  );

  const openVulnMetricsByTimeCountsData = useAtomValue(
    openVulnMetricsByTimeCountsAtom,
  );
  const closedVulnMetricsByTimeCountsData = useAtomValue(
    closedVulnMetricsByTimeCountsAtom,
  );

  const openVulnMetricsTooltip = useAtomValue(openVulnMetricsTooltipAtom);
  const closedVulnMetricsTooltip = useAtomValue(closedVulnMetricsTooltipAtom);

  const metricsView = useAtomValue(metricsViewAtom);
  const criticalityFilters = useAtomValue(criticalityFiltersAtom);
  const selectedHostIds = useAtomValue(appliedHostIdsAtom);
  const policyId = useAtomValue(policyIdFilterAtom);

  function getSeverityChartsDrillDownUrl(
    statusType: string,
    label: string,
    _sourceType: string,
    age_min_exclusive?: number,
  ) {
    return getDrillDownUrl({
      policyId: policyId,
      status_in: [statusType],
      criticality_in: [getSeverityValue(Severity[label])],
      hosts_in: selectedHostIds,
      age_min: age_min_exclusive,
    });
  }

  function getTimeChartsDrillDownUrl(
    statusType: string,
    label: string,
    _sourceType: string,
  ) {
    const [age_min, age_max] = getTimeLabelValue(label);
    return getDrillDownUrl({
      policyId: policyId,
      status_in: [statusType],
      hosts_in: selectedHostIds,
      age_min: age_min,
      age_max: age_max,
    });
  }

  function getCalculatedCountsDataset(countsData) {
    // add all data set, if there are multiple datasets
    const clonedDatasets = _.cloneDeep(countsData);
    return clonedDatasets.length > 1
      ? [mergeArrays(clonedDatasets), ...clonedDatasets]
      : clonedDatasets;
  }

  const getChartHeader = (totalCount, title, subTitleCount, subTitle) => {
    return (
      <CardHeader>
        <Row>
          <Col className="d-flex flex-row align-items-baseline">
            <h1 className="d-inline mb-0">{totalCount}</h1>
            <h3 className="card-category px-1">{title}</h3>
          </Col>
          <Col>
            <div className="d-flex flex-row d-inline float-right align-items-baseline">
              <h1 className="d-inline mb-0">{subTitleCount}</h1>
              <h3 className="card-category px-1">{subTitle}</h3>
            </div>
          </Col>
        </Row>
      </CardHeader>
    );
  };

  function getVulnMetricsTooltipFunction(countsDatasets) {
    return function (item, data) {
      const datasetLabel = data.datasets[item.datasetIndex].label;
      if (datasetLabel && countsDatasets) {
        return [
          datasetLabel,
          `Average Days: ${data.datasets[item.datasetIndex].data[
            item.index
          ].toFixed(1)}`,
          `Findings Count: ${countsDatasets[item.datasetIndex][item.index]}`,
        ];
      } else {
        return (
          data.labels[item.index] +
          ': ' +
          data.datasets[item.datasetIndex].data[item.index]
        );
      }
    };
  }

  function getVulnMetricsDoughnutTooltipFunction(countsDatasets) {
    return function (item, data) {
      const datasetLabel = data.datasets[item.datasetIndex].label;
      if (datasetLabel && countsDatasets) {
        return [
          datasetLabel,
          `Findings Count: ${countsDatasets[item.datasetIndex][item.index]}`,
        ];
      } else {
        return (
          data.labels[item.index] +
          ': ' +
          data.datasets[item.datasetIndex].data[item.index]
        );
      }
    };
  }

  function getVulnMetricsDoughnutTooltipTitleFunction() {
    return function (item, data) {
      return data.labels[item[0].index];
    };
  }

  return (
    <Fragment>
      {openVulnMetricsBySeverityAverageDayTotalVulnData &&
        metricsView === MetricsView.Severity && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Bar}
                title=""
                labels={['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal']}
                tooltipLabels={openVulnMetricsTooltip}
                datasets={
                  openVulnMetricsBySeverityAverageDayTotalVulnData.metricsData
                }
                severityFilters={criticalityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getSeverityChartsDrillDownUrl('Failed', label, sourceType)
                }
                customChartHeader={getChartHeader(
                  openVulnMetricsBySeverityAverageDayTotalVulnData.averageDaysOpenClose,
                  'days (Average Time In Failed Status)',
                  openVulnMetricsBySeverityAverageDayTotalVulnData.totalVulnsCount,
                  'Failed Findings',
                )}
                tooltipsCallbackFunction={getVulnMetricsTooltipFunction(
                  getCalculatedCountsDataset(
                    openVulnMetricsBySeverityCountsData?.metricsData,
                  ),
                )}
                yAxesLabel={'Average Time In Failed Status Days'}
                operationType={'Average'}
                countDatasets={openVulnMetricsBySeverityCountsData?.metricsData}
              />
            </div>
          </>
        )}
      {closeVulnMetricsBySeverityAverageDayTotalVulnData &&
        metricsView === MetricsView.Severity && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Bar}
                title=""
                labels={['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal']}
                tooltipLabels={closedVulnMetricsTooltip}
                datasets={
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.metricsData
                }
                severityFilters={criticalityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getSeverityChartsDrillDownUrl('Passed', label, sourceType, 0)
                }
                customChartHeader={getChartHeader(
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.averageDaysOpenClose,
                  'days (Average Time To Pass)',
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.totalVulnsCount,
                  'Passed Findings',
                )}
                tooltipsCallbackFunction={getVulnMetricsTooltipFunction(
                  getCalculatedCountsDataset(
                    closedVulnMetricsBySeverityCountsData?.metricsData,
                  ),
                )}
                yAxesLabel={'Average Time To Pass Days'}
                operationType={'Average'}
                countDatasets={
                  closedVulnMetricsBySeverityCountsData?.metricsData
                }
              />
            </div>
          </>
        )}

      {openVulnMetricsByTimeCountsData && metricsView === MetricsView.Time && (
        <>
          <div className="col-md-6">
            <BarOrDoughnutChart
              chartType={ChartType.Doughnut}
              title=""
              labels={timeViewLabels}
              tooltipLabels={openVulnMetricsTooltip}
              datasets={openVulnMetricsByTimeCountsData}
              severityFilters={criticalityFilters}
              getDrillDownUrl={(label, sourceType) =>
                getTimeChartsDrillDownUrl('Failed', label, sourceType)
              }
              customChartHeader={getChartHeader(
                openVulnMetricsBySeverityAverageDayTotalVulnData?.averageDaysOpenClose,
                'days (Average Time In Failed Status)',
                openVulnMetricsBySeverityAverageDayTotalVulnData?.totalVulnsCount,
                'Failed Findings',
              )}
              tooltipsCallbackFunction={getVulnMetricsDoughnutTooltipFunction(
                getCalculatedCountsDataset(openVulnMetricsByTimeCountsData),
              )}
              tooltipsTitleCallbackFunction={getVulnMetricsDoughnutTooltipTitleFunction()}
            />
          </div>
        </>
      )}
      {closedVulnMetricsByTimeCountsData &&
        metricsView === MetricsView.Time && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Doughnut}
                title=""
                labels={timeViewLabels}
                tooltipLabels={closedVulnMetricsTooltip}
                datasets={closedVulnMetricsByTimeCountsData}
                severityFilters={criticalityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getTimeChartsDrillDownUrl('Passed', label, sourceType)
                }
                customChartHeader={getChartHeader(
                  closeVulnMetricsBySeverityAverageDayTotalVulnData?.averageDaysOpenClose,
                  'days (Average Time To Pass)',
                  closeVulnMetricsBySeverityAverageDayTotalVulnData?.totalVulnsCount,
                  'Passed Findings',
                )}
                tooltipsCallbackFunction={getVulnMetricsDoughnutTooltipFunction(
                  getCalculatedCountsDataset(closedVulnMetricsByTimeCountsData),
                )}
                tooltipsTitleCallbackFunction={getVulnMetricsDoughnutTooltipTitleFunction()}
              />
            </div>
          </>
        )}
    </Fragment>
  );
};

export default Charts;
